// GlassCard.tsx — MEA-tyylinen lasiskortti (glassmorphism + gradient border)
import type { CSSProperties, ReactNode } from 'react';
// Kortin kulmapyoristys on jaettu token (natiivi piirtaa saman kortin).
import { TOKENS } from '../styles/designTokens';

interface GlassCardProps {
  children: ReactNode;
  accent?: string;         // border-left color
  gradient?: [string, string]; // gradient colors
  onClick?: () => void;
  style?: CSSProperties;
  animate?: boolean;
}

export default function GlassCard({
  children, accent, gradient, onClick, style, animate = true,
}: GlassCardProps) {
  const borderStyle = accent ? { borderLeft: `4px solid ${accent}` } : {};
  const bgGradient = gradient
    ? `linear-gradient(135deg, ${gradient[0]}15, ${gradient[1]}08)`
    : undefined;

  return (
    <div
      onClick={onClick}
      style={{
        background: bgGradient ?? 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: TOKENS.radius.card,
        padding: '18px 20px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.03)',
        border: '1px solid rgba(255,255,255,0.6)',
        cursor: onClick ? 'pointer' : undefined,
        transition: animate ? 'transform 0.2s, box-shadow 0.2s' : undefined,
        ...borderStyle,
        ...style,
      }}
      onMouseEnter={onClick ? (e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
      } : undefined}
      onMouseLeave={onClick ? (e) => {
        (e.currentTarget as HTMLDivElement).style.transform = '';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.03)';
      } : undefined}
    >
      {children}
    </div>
  );
}

// Status dot (traffic light)
export function StatusDot({ color }: { color: 'green' | 'red' | 'orange' | 'gray' }) {
  const colors = { green: '#16a34a', red: '#dc2626', orange: '#d97706', gray: '#94a3b8' };
  return (
    <span style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: colors[color],
      boxShadow: `0 0 6px ${colors[color]}40`,
      marginRight: 8,
      flexShrink: 0,
    }} />
  );
}

// Section title
export function SectionTitle({ children, icon }: { children: ReactNode; icon?: string }) {
  return (
    <h3 style={{
      fontSize: 13,
      fontWeight: 700,
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      margin: '28px 0 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontFamily: "var(--font-ui)",
    }}>
      {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
      {children}
    </h3>
  );
}

// Big action button (MEA-style gradient)
export function ActionButton({
  children, onClick, disabled, variant = 'primary', style,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'success';
  style?: React.CSSProperties;
}) {
  const gradients = {
    primary: 'linear-gradient(135deg, #2a6a8a, #3c7c9c, #5ba8c8)',
    secondary: 'linear-gradient(135deg, #475569, #64748b)',
    success: 'linear-gradient(135deg, #16a34a, #22c55e)',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '16px 24px',
        background: disabled ? '#cbd5e1' : gradients[variant],
        color: '#fff',
        border: 'none',
        borderRadius: 14,
        fontSize: 16,
        fontWeight: 700,
        fontFamily: "var(--font-ui)",
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'transform 0.15s, opacity 0.15s',
        boxShadow: disabled ? 'none' : '0 4px 16px rgba(60,124,156,0.3)',
        letterSpacing: '-0.01em',
        ...style,
      }}
    >
      {children}
    </button>
  );
}
